import i18n from "../i18n";
import React, { useState, useEffect, useMemo, useRef, useCallback, useLayoutEffect, memo } from 'react';
import { useLayoutImage } from "../context/LayoutContext";
import axios from 'axios';
import './TableSelector.css';
import config from '../config';
import { renderSeatDots, computeSelectionCounts, getReservedCount } from './shared/renderSeatDots';
import TableConnections from './shared/TableConnections';
import { useIsMobile } from '../Responsive';
import { bbqTerm } from '../utils/bbqTerms';

const tr = (...args) => i18n.t(...args);

const TableSelector = ({ reservationTime, selectedAssignedTables = [], onTablesSelected, areaFilter, tableFilter, onAreaTabChange, service_view, people = 0, activeReservationId = null, onTableClick, tablesOverride = null}) => {

  const backgroundImage = useLayoutImage();
  const [rawTables, setRawTables] = useState([]);
  const [areas, setAreas] = useState([]);
  const [selectedTables, setSelectedTables] = useState(selectedAssignedTables);
  const [activeTab, setActiveTab] = useState(0);
  const isMobile = useIsMobile();

  
const tables = useMemo(() => {
  const base = Array.isArray(rawTables) ? rawTables : [];
  return base.map((t) => {
    const isSelected = selectedTables.includes(t.id);
    const isOwnReservationTable =
      activeReservationId != null &&
      activeReservationId !== '' &&
      Number(t.reserved_id) === Number(activeReservationId);

    if (isSelected) {
      return {
        ...t,
        isAvailable: true,
        reserved_id: isOwnReservationTable ? t.reserved_id : null,
        reserved_people: isOwnReservationTable ? t.reserved_people : null,
        nextReservation: isOwnReservationTable ? t.nextReservation : '',
      };
    }

    return isOwnReservationTable
      ? { ...t, isAvailable: true }
      : t;
  });
}, [rawTables, selectedTables, activeReservationId]);

  // --- Mobile-fit scaling for the canvas (keeps DB coordinates intact, avoids horizontal overflow)
  const viewportRef = useRef(null);
  const tabsHeaderRef = useRef(null);
  const layoutTabRef = useRef(null);
  const [tabsCanLeft, setTabsCanLeft] = useState(false);
  const [tabsCanRight, setTabsCanRight] = useState(false);

  const getScrollParent = (node) => {
    let el = node?.parentElement;
    while (el) {
      const style = window.getComputedStyle(el);
      const oy = style.overflowY;
      if (oy === 'auto' || oy === 'scroll') return el;
      el = el.parentElement;
    }
    return null;
  };
  
  const updateTabsScroll = useCallback(() => {
    const el = tabsHeaderRef.current;
    if (!el) return;
    const left = el.scrollLeft > 1;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setTabsCanLeft(left);
    setTabsCanRight(right);
  }, []);  
  
  const [canvasScale, setCanvasScale] = useState(1);
  
  const baseCanvas = useMemo(() => {
    return activeTab === 0
      ? { w: 800, h: 800 }
      : { w: 565, h: 500 };
  }, [activeTab]);
  
  const recomputeScale = useCallback(() => {
    if (!isMobile || !viewportRef.current) {
      setCanvasScale(1);
      return;
    }
  
    const vw = viewportRef.current.clientWidth || 0;
  
    // Use *available visible height* instead of a fixed %.
    // This makes the canvas fill the screen better in ReservationForm,
    // while still fitting nicely inside modals.
    let maxH = Math.floor(window.innerHeight * 0.92);
    try {
      const rect = viewportRef.current.getBoundingClientRect();
      const available = window.innerHeight - rect.top - 24; // little bottom padding
      if (Number.isFinite(available) && available > 0) {
        maxH = Math.min(maxH, Math.floor(available));
      }
    } catch {}
  
    maxH = Math.max(220, maxH);
  
    const sW = vw / baseCanvas.w;
    const sH = maxH / baseCanvas.h;
    const s = Math.max(0.35, Math.min(1, sW, sH));
    setCanvasScale(s);
  }, [isMobile, baseCanvas.w, baseCanvas.h]);
  
  useLayoutEffect(() => {
    recomputeScale();
  }, [recomputeScale]);
  
  useEffect(() => {
    if (!isMobile) return;
  
    // On mobile, the selector often mounts while still off-screen (user hasn't scrolled yet).
    // That makes rect.top large -> available height small -> initial scale too small.
    // So we recompute on scroll (throttled) + after paint.
    let raf = 0;
    const scheduleRecompute = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        recomputeScale();
      });
    };
  
    // initial + after paint
    scheduleRecompute();
    requestAnimationFrame(scheduleRecompute);
  
    const onResizeOrScroll = () => scheduleRecompute();
  
    window.addEventListener('resize', onResizeOrScroll);
    window.addEventListener('scroll', onResizeOrScroll, { passive: true });
  
    const scrollParent = getScrollParent(viewportRef.current);
    if (scrollParent) {
      scrollParent.addEventListener('scroll', onResizeOrScroll, { passive: true });
    }
  
    // URL bar / visual viewport changes on mobile
    const vv = window.visualViewport;
    if (vv) vv.addEventListener('resize', onResizeOrScroll);
  
    // ResizeObserver for container changes
    let ro;
    try {
      ro = new ResizeObserver(() => scheduleRecompute());
      if (viewportRef.current) ro.observe(viewportRef.current);
    } catch {}
  
    return () => {
      window.removeEventListener('resize', onResizeOrScroll);
      window.removeEventListener('scroll', onResizeOrScroll);
      if (vv) vv.removeEventListener('resize', onResizeOrScroll);
      if (ro) ro.disconnect();
      if (raf) cancelAnimationFrame(raf);
      if (scrollParent) {
        scrollParent.removeEventListener('scroll', onResizeOrScroll);
      }
    };
  }, [isMobile, recomputeScale]);


  //console.log('selectedAssignedTables', selectedAssignedTables);

  const filteredAreas = useMemo(
    () => areas.filter(a => (areaFilter ? areaFilter(a) : true)),
    [areas, areaFilter]
  );

  useEffect(() => {
    if (!isMobile) return;
    const el = tabsHeaderRef.current;
    if (!el) return;
  
    // Mobile browsers (notably iOS) may restore horizontal scroll position on mount
    // or apply it after fonts/layout settle. Force-start a few times.
    const forceStart = () => {
      const el = tabsHeaderRef.current;
      if (!el) return;
    
      // 1) Try to make Layout tab visible (stronger than scrollLeft=0)
      if (layoutTabRef.current?.scrollIntoView) {
        layoutTabRef.current.scrollIntoView({
          block: 'nearest',
          inline: 'start',
          behavior: 'auto',
        });
      }
    
      // 2) And also hard-reset scrollLeft (some browsers ignore scrollIntoView sometimes)
      try {
        el.scrollTo({ left: 0, behavior: 'auto' });
      } catch {
        el.scrollLeft = 0;
      }
    
      requestAnimationFrame(updateTabsScroll);
    };

  
    forceStart();
    requestAnimationFrame(forceStart);
  
    const onResize = () => forceStart();
    const onVis = () => {
      if (document.visibilityState === 'visible') forceStart();
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [isMobile, filteredAreas.length, updateTabsScroll]);

  useLayoutEffect(() => {
    if (!isMobile) return;
    const el = tabsHeaderRef.current;
    if (!el) return;
  
    if (activeTab === 0) {
      el.scrollLeft = 0;
      requestAnimationFrame(updateTabsScroll);
      return;
    }
  
    const btns = el.querySelectorAll('button');
    const btn = btns?.[activeTab];
    if (btn && typeof btn.scrollIntoView === 'function') {
      btn.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'auto' });
      requestAnimationFrame(updateTabsScroll);
    }
  }, [isMobile, activeTab, filteredAreas.length, updateTabsScroll]);

  useEffect(() => {
    if (!onAreaTabChange) return;
    if (activeTab === 0) {
      onAreaTabChange(null);          // Layout ⇒ all areas
    } else {
      const area = filteredAreas[activeTab - 1];
      onAreaTabChange(area ? area.id : null);
    }
  }, [activeTab, filteredAreas, onAreaTabChange]);

  useEffect(() => {
    if (activeTab > filteredAreas.length) setActiveTab(0);
  }, [filteredAreas.length]);

  const fetchAvailableTables = async () => {
    const url = tr('{{API_BASE_URL}}/tables/availability/{{reservationTime}}', {
      API_BASE_URL: config.API_BASE_URL,
      reservationTime
    });
    const response = await axios.get(url, { withCredentials: true, timeout: 15000 });
    // Keep the server response raw and derive the painted state from current selection/edit context.
    // This avoids stale colors when selectedAssignedTables arrives slightly after the availability fetch.
    setRawTables(Array.isArray(response.data) ? response.data : []);
  };

  const fetchAreas = async () => {
    try {
      const response = await axios.get(tr('{{API_BASE_URL}}/areas', {
        API_BASE_URL: config.API_BASE_URL
      }), { withCredentials: true, timeout: 15000 });
      //console.log('fetchAreas',response.data)
      setAreas(response.data);
    } catch (error) {
      console.error('Error fetching areas:', error);
    }
  }

  useEffect(() => { fetchAreas(); }, []);

  useEffect(() => {
    if (Array.isArray(tablesOverride)) {
      setRawTables(tablesOverride);
      return;
    }
    fetchAvailableTables();
  }, [reservationTime, tablesOverride]);

  useEffect(() => {
    const next = Array.isArray(selectedAssignedTables) ? selectedAssignedTables : [];
    // Sync local selection when parent data changes (even in service_view)
    if (JSON.stringify(selectedTables) !== JSON.stringify(next)) {
      setSelectedTables(next);
    }
  }, [selectedAssignedTables]);

  useEffect(() => { 
    onTablesSelected(selectedTables);
  }, [selectedTables]);

  useEffect(() => {
    if (!isMobile) return;
    const el = tabsHeaderRef.current;
    if (!el) return;
  
    if (activeTab === 0) {
      layoutTabRef.current?.scrollIntoView?.({
        block: 'nearest',
        inline: 'start',
        behavior: 'auto',
      });
      try { el.scrollTo({ left: 0, behavior: 'auto' }); } catch { el.scrollLeft = 0; }
    } else {
      const btn = el.querySelector('button.active');
      btn?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'auto' });
    }
    requestAnimationFrame(updateTabsScroll);
    
  }, [isMobile, activeTab, filteredAreas.length, updateTabsScroll]);

  const toggleTableSelection = (tableId) => {
    setSelectedTables((prevSelected) =>
      prevSelected.includes(tableId)
        ? prevSelected.filter((id) => id !== tableId) // Unselect if already selected
        : [...prevSelected, tableId] // Select if not already selected
    );
  };

  const capacityClass = (table, p) => {
    if (!table?.isAvailable) return '';                // exclude already-reserved
    const cap = Number(table?.capacity ?? 0);
    const n   = Number(p ?? 0);
    if (!n) return '';                                 // no hint if party size unknown
    const delta = cap - n;
    if (delta < 0) return 'capacity-insufficient';     // gray
    if (delta === 0) return 'capacity-equal';          // yellow
    if (delta === 1 || delta === 2) return 'capacity-plus'; // orange
    return '';                                         // keep default green
  };

  const CapacityLegend = () => {
    return (
      <div className="capacity-legend">
        <span className="legend-item"><span className="legend-dot orange"   />{tr('Preferred')}</span>
        <span className="legend-item"><span className="legend-dot yellow"   />{tr('+1/+2 seats')}</span>
        <span className="legend-item"><span className="legend-dot green"    />{tr('Available')}</span>
        <span className="legend-item"><span className="legend-dot gray"     />{tr('No capacity')}</span>
        <span className="legend-item"><span className="legend-dot darkred"  />{tr('Reserved')}</span>
        <span className="legend-item"><span className="legend-dot blue"     />{tr('Selected')}</span>
      </div>
    );
  };

  // Reserve-takeover preview only.
  // The real takeover now happens safely on the backend when the reservation is saved.
  const reassignReservedTable = async (table) => {
    const prevReservationId = Number(table?.reserved_id);
    if (!prevReservationId) return;

    const alreadySelected = selectedTables.includes(Number(table.id));
    if (alreadySelected) {
      toggleTableSelection(table.id);
      return;
    }

    const ok = window.confirm(
      bbqTerm(
        "The table is already reserved for this time slot.\n\nIf you continue, when you save this reservation and the old overlapping reservation is already active (sitting or later), it will be completed automatically, its active meats/actions will be finalized, and the table will be released for this reservation. If the old reservation is still before sitting, only this selected table will be removed from that old reservation and assigned here.\n\nDo you want to continue?"
      )
    );
    if (!ok) return;

    toggleTableSelection(table.id);
  };

  const renderTables = () => {

    const perTableCounts = computeSelectionCounts(people, selectedTables, tables);
    const reservationsFromTables = (() => {
      const map = {};
      (tables || []).forEach(t => {
        if (t?.reserved_id && map[t.reserved_id] == null) {
          map[t.reserved_id] = { id: Number(t.reserved_id), people: Number(t.reserved_people || 0) };
        }
      });
      return Object.values(map);
    })();

    if (activeTab === 0) {
      // Layout tab: use monitor_* coords and respect areaFilter too
      const allowedAreaIds = new Set(filteredAreas.map(a => +a.id));
      return (
        <>
          {tables
            .filter(t => (tableFilter ? tableFilter(t) : true))
            .filter(t => (areaFilter ? allowedAreaIds.has(+t.area) : true))
            .map(table => {
            return (
              <div
                key={table.id}
                className={`table ${
                  selectedTables.includes(table.id)
                    ? 'selected'
                    : !table.isAvailable
                      ? 'reserved'
                      : `available ${capacityClass(table, people)}`.trim()
                }`}
                style={{
                  left: table.monitor_x,
                  top: table.monitor_y,
                  width: table.width,
                  height: table.height,
                  cursor: (typeof onTableClick === 'function' || table.isAvailable)
                    ? 'pointer'
                    : 'not-allowed',
                }}
                onClick={() => {
                  // If a parent supplied a custom handler, use it
                  if (typeof onTableClick === 'function') {
                    onTableClick(table);
                    return;
                  }
                
                  // Default behaviour (existing logic)
                  if (table.isAvailable) {
                    toggleTableSelection(table.id);
                  } else {
                    reassignReservedTable(table);
                  }
                }}
              >
                <b>{table.number}</b>
                <br />
                <i style={{ fontSize: '10px' }}>{table.isAvailable ? '' : table.nextReservation}</i>
                {renderSeatDots(
                  table,
                  2,
                  2,
                  {
                    // Ignore "reserved" red for THIS reservation; use preview instead
                    reservedCount:
                      (table.reserved_id && Number(table.reserved_id) !== Number(activeReservationId))
                        ? getReservedCount(table, tables, reservationsFromTables)     // other reservations -> red
                        : (perTableCounts[Number(table.id)] || 0),                    // this reservation -> preview (clears on unselect)
                     reservedClass: 'reserved-seat-red'
                  }
                )}
              </div>
            );
          })
          }
          <TableConnections
            tables={tables}
            groupBy={(t) => (Number(t.reserved_id) === Number(activeReservationId) ? null : t.reserved_id)}
            coord="monitor"
            stroke="darkRed"
            strokeWidth={2}
          />
          <TableConnections
            tables={tables}
            groupBy={(t) => (selectedTables.includes(t.id) ? '__NEW_SELECTION__' : null)}
            coord="monitor"
            stroke="darkBlue"
            strokeWidth={1}
            dashArray="2,2"
          />
        </>
      );
    } else {
      // Area tab: only tables in the selected area, use area_* coords
      const selectedArea = filteredAreas[activeTab - 1]; // -1 because first tab is Layout
      const areaTables = tables
        .filter((table) => +(table.area) === +(selectedArea?.id))
        .filter(t => (tableFilter ? tableFilter(t) : true));
  
      return (
        <>
          {areaTables.map(table => {
            return (
              <div
                key={table.id}
                className={`table ${
                  selectedTables.includes(table.id)
                    ? 'selected'
                    : !table.isAvailable
                      ? 'reserved'
                      : `available ${capacityClass(table, people)}`.trim()
                }`}
                style={{
                  left: table.area_x,
                  top: table.area_y,
                  width: table.width * (isMobile ? 1.4 : 1.2),
                  height: table.height * (isMobile? 1.4 : 1.2),
                  cursor: (typeof onTableClick === 'function' || table.isAvailable)
                    ? 'pointer'
                    : 'not-allowed',
                }}
                onClick={() => {
                  if (typeof onTableClick === 'function') {
                    onTableClick(table);
                    return;
                  }
                
                  if (table.isAvailable) {
                    toggleTableSelection(table.id);
                  } else {
                    reassignReservedTable(table);
                  }
                }}
              >
                <b>{table.number}</b>
                <br />
                <i style={{ fontSize: '10px' }}>{table.isAvailable ? '' : table.nextReservation}</i>
                {renderSeatDots(
                  table,
                  2,
                  2,
                  {
                    reservedCount:
                      (table.reserved_id && Number(table.reserved_id) !== Number(activeReservationId))
                        ? getReservedCount(table, areaTables, reservationsFromTables)  // other reservations -> red
                        : (perTableCounts[Number(table.id)] || 0),  
                    reservedClass: 'reserved-seat-red'
                  },
                  isMobile?1.4:1.2
                )}
              </div>
            );
          })}
          <TableConnections
            tables={areaTables}
            groupBy={(t) => (Number(t.reserved_id) === Number(activeReservationId) ? null : t.reserved_id)}
            coord="area"
            stroke="darkRed"
            strokeWidth={2}
          />
          <TableConnections
            tables={areaTables}
            groupBy={(t) => (selectedTables.includes(t.id) ? '__NEW_SELECTION__' : null)}
            coord="area"
            stroke="darkBlue"
            strokeWidth={1}
            dashArray="2,2"
          />
        </>
      );
    }
  };

  const scaledCanvas = useMemo(() => {
    return {
      w: Math.round(baseCanvas.w * canvasScale),
      h: Math.round(baseCanvas.h * canvasScale),
    };
  }, [baseCanvas.w, baseCanvas.h, canvasScale]);
  

  return (
    <div className={`table-selector ${service_view ? 'ts-service-view' : ''}`}>
      {/* In service-view (e.g. Sitting modal), hide the big header to keep the modal compact */}
      {(!service_view && (typeof onTableClick != 'function')) && (
        <h3
          style={{
            display: 'ruby-text',
            margin: isMobile ? '2px 0 4px 0' : undefined,
            marginBottom: isMobile ? 4 : 0,
          }}
        >
          {tr('Select Tables to Reserve:')}
          {!isMobile && (<CapacityLegend />)}
        </h3>
      )}
      {/* Tabs Header */}
      {isMobile ? (
        <div className={`ts-scroll-wrap ${tabsCanLeft ? 'can-left' : ''} ${tabsCanRight ? 'can-right' : ''}`}>
          <button
            type="button"
            className="ts-scroll-arrow left"
            aria-label="Scroll tabs left"
            onClick={() => tabsHeaderRef.current?.scrollBy({ left: -80, behavior: 'smooth' })}
          >
            {'\u2039'}
          </button>
      
          <div className="tabs-header ts-scroll-viewport" ref={tabsHeaderRef} onScroll={updateTabsScroll}>
            <button
              ref={layoutTabRef}
              className={activeTab === 0 ? 'active' : ''}
              onClick={() => setActiveTab(0)}
            >
              {tr('Layout')}
            </button>
            {filteredAreas.map((area, index) => (
              <button
                key={area.id ?? index}
                className={activeTab === index + 1 ? 'active' : ''}
                onClick={() => setActiveTab(index + 1)}
              >
                {area.name}
              </button>
            ))}
          </div>
      
          <button
            type="button"
            className="ts-scroll-arrow right"
            aria-label="Scroll tabs right"
            onClick={() => tabsHeaderRef.current?.scrollBy({ left: 80, behavior: 'smooth' })}
          >
            {'\u203A'}
          </button>
        </div>
      ) : (
        <div className="tabs-header">
          <button
            ref={layoutTabRef}
            className={activeTab === 0 ? 'active' : ''}
            onClick={() => setActiveTab(0)}
          >
            {tr('Layout')}
          </button>
          {filteredAreas.map((area, index) => (
            <button
              key={area.id ?? index}
              className={activeTab === index + 1 ? 'active' : ''}
              onClick={() => setActiveTab(index + 1)}
            >
              {area.name}
            </button>
          ))}
        </div>
      )}

      {/* Tabs Content */}
      {isMobile ? (
        <div
          ref={viewportRef}
          className="ts-viewport"
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          <div style={{ width: scaledCanvas.w, height: scaledCanvas.h }}>
            <div
              className={activeTab ? 'area-layout' : 'layout'}
              style={{
                backgroundImage: activeTab ? 'none' : `url(${backgroundImage})`,
                width: baseCanvas.w,
                height: baseCanvas.h,
                transform: `scale(${canvasScale})`,
                transformOrigin: 'top left',
              }}
            >
              {renderTables()}
            </div>
          </div>
        </div>
      ) : (
        <div
          className={activeTab ? 'area-layout' : 'layout'}
          style={{ backgroundImage: activeTab ? 'none' : `url(${backgroundImage})` }}
        >
          {renderTables()}
        </div>
      )}
      {isMobile && !service_view && (
        <div style={{ marginTop: 6 }}>
          <CapacityLegend />
        </div>
      )}
    </div>
  );
};

const shallowArrayEqual = (a, b) =>
  a === b || (Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((v, i) => v === b[i]));

export default memo(TableSelector, (prev, next) => {
  return (
    prev.reservationTime === next.reservationTime &&
    prev.people === next.people &&
    prev.activeReservationId === next.activeReservationId &&
    shallowArrayEqual(prev.selectedAssignedTables, next.selectedAssignedTables) &&
    prev.areaFilter === next.areaFilter &&
    prev.tableFilter === next.tableFilter &&
    prev.onTableClick === next.onTableClick
  );
});
